export type ThemeMode = 'auto' | 'light' | 'dark';

const KEY = 'flightdeck-theme';
const query = () => window.matchMedia('(prefers-color-scheme: light)');

/**
 * LIGHT, NOT THE MACHINE'S SETTING.
 *
 * This was 'auto', which followed the operating system. Most developers run
 * their machines dark, so most people opened FlightDeck into the deep navy and
 * never saw the daylight half at all — including Vinnie, who chose the palette
 * for being "modern bright and easy on the eyes seeing that developers spend a
 * lot of time looking at the screen" and then found the dark one waiting for
 * him. A default that quietly hides the design it was chosen for is the wrong
 * default, however reasonable it sounds.
 *
 * Dark is still there and still one click away in Account → Appearance, and
 * 'auto' is still offered for anyone who wants their machine to decide.
 */
const DEFAULT_MODE: ThemeMode = 'light';

export function readThemeMode(): ThemeMode {
  const v = localStorage.getItem(KEY);
  /* 'auto' is now STORED rather than represented by an empty slot. It used to
     be the fallback, so choosing it meant deleting the key — which is
     indistinguishable from never having chosen anything. Now that the fallback
     is light, that ambiguity would silently overturn a deliberate choice of
     'auto' on the next load. */
  return v === 'light' || v === 'dark' || v === 'auto' ? v : DEFAULT_MODE;
}

/** Resolves the mode to an actual theme and stamps it on <html>, which is what the CSS reads. */
export function applyTheme(mode: ThemeMode): void {
  const resolved = mode === 'auto' ? (query().matches ? 'light' : 'dark') : mode;
  document.documentElement.dataset.theme = resolved;
}

export function setThemeMode(mode: ThemeMode): void {
  localStorage.setItem(KEY, mode);
  applyTheme(mode);
}

/**
 * Stamps the stored theme on <html> before React renders anything.
 *
 * Nothing did this. applyTheme was called from one place — a useEffect inside
 * AccountPanel — which lives inside the signed-in shell, so every screen before
 * sign-in ran with no data-theme at all. Bare :root in styles.css is the dark
 * palette, so the sign-in screen, the password screen and the invitation screen
 * were unconditionally dark no matter what anyone had chosen. Not a preference
 * being ignored so much as never being asked for.
 */
export function applyStoredTheme(): void {
  applyTheme(readThemeMode());
}

/** Keeps the page in step with the OS while the user is on 'auto'. */
export function watchSystemTheme(getMode: () => ThemeMode): () => void {
  const mq = query();
  const onChange = () => {
    if (getMode() === 'auto') applyTheme('auto');
  };
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
